'use client'

import { cn } from '@heroui/react'

import { Icon } from '@/components/ui/icon'
import type { IconName } from '@/config/icons'
import type { InquiryContactMethod } from '@/lib/validation/input/inquiry-schema'

const METHODS: { value: InquiryContactMethod; label: string; icon: IconName }[] = [
  { value: 'email', label: 'Email', icon: 'cw-email' },
  { value: 'sms', label: 'Text', icon: 'chat-round-dots-linear' },
  { value: 'call', label: 'Call', icon: 'cw-phone' },
]

interface ContactMethodPickerProps {
  value: InquiryContactMethod
  onChange: (value: InquiryContactMethod) => void
  className?: string
}

/**
 * Three-way "how should we reach you" segmented control. Same sliding-thumb
 * treatment as the fleet form's two-way toggle, extended with Email because
 * the general contact and quote forms do not require a phone number.
 */
export function ContactMethodPicker({ value, onChange, className }: ContactMethodPickerProps) {
  const index = METHODS.findIndex((m) => m.value === value)
  return (
    <div
      role="radiogroup"
      aria-label="Preferred contact method"
      className={cn(
        'relative inline-grid w-full grid-cols-3 rounded-full border border-border bg-background p-1 sm:w-auto',
        className
      )}
    >
      <span
        aria-hidden="true"
        className="pointer-events-none absolute inset-y-1 left-1 w-[calc(33.333%-0.25rem)] rounded-full bg-accent shadow-sm transition-transform duration-200 ease-out"
        style={{ transform: `translateX(${Math.max(0, index) * 100}%)` }}
      />
      {METHODS.map((m) => {
        const active = m.value === value
        return (
          <button
            key={m.value}
            type="button"
            role="radio"
            aria-checked={active}
            aria-label={`Contact me by ${m.label.toLowerCase()}`}
            onClick={() => onChange(m.value)}
            className={cn(
              'relative z-10 flex min-h-11 items-center justify-center gap-1.5 rounded-full px-4 py-1.5 text-sm font-medium transition-colors',
              active ? 'text-accent-foreground' : 'text-muted hover:text-foreground'
            )}
          >
            <Icon icon={m.icon} className="text-base" aria-hidden="true" />
            <span className="whitespace-nowrap">{m.label}</span>
          </button>
        )
      })}
    </div>
  )
}
